import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type {
  CreditDraftInput,
  CreditDraftSource,
  CreditParameterSet,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';

import { mensagemDoErro, type EscopoDoEditor } from './escopo';
import { noFuturo, textoDasAprovacoes } from './valores';

/**
 * Abrir rascunho: de onde copiar os valores e a observacao.
 *
 * A copia do padrao global so aparece onde a instalacao tem a governanca. Sem
 * escolha, o servidor copia a versao ativa, ou os valores documentados.
 */
export function AbrirRascunhoDialog({
  aberto,
  aoFechar,
  escopo,
  temAtiva,
  padraoGlobalDisponivel,
  aoAbrir,
}: {
  aberto: boolean;
  aoFechar: () => void;
  escopo: EscopoDoEditor;
  temAtiva: boolean;
  padraoGlobalDisponivel: boolean;
  aoAbrir: (versao: CreditParameterSet) => void;
}) {
  const [origem, setOrigem] = useState<CreditDraftSource>('ativa');
  const [observacao, setObservacao] = useState('');

  const abrir = useMutation({
    mutationFn: () => {
      const entrada: CreditDraftInput = {};
      if (observacao.trim()) entrada.notes = observacao.trim();
      // So envia a origem quando e o padrao global: a instalacao sem governanca
      // recusaria o campo, e a copia da ativa ja e o padrao do servidor.
      if (origem === 'padrao_global') entrada.source = 'padrao_global';
      return escopo.api.createDraft(entrada);
    },
    onSuccess: (versao) => {
      setObservacao('');
      setOrigem('ativa');
      aoAbrir(versao);
    },
  });

  return (
    <Modal
      open={aberto}
      onClose={aoFechar}
      title="Abrir rascunho"
      description="O rascunho e a unica versao que muda. A versao ativa continua valendo ate outra pessoa ativar o rascunho."
      footer={
        <>
          <Button variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button loading={abrir.isPending} onClick={() => abrir.mutate()}>
            Abrir rascunho
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {abrir.isError ? (
          <Alert tone="danger" title="Nao foi possivel abrir o rascunho">
            {mensagemDoErro(abrir.error)}
          </Alert>
        ) : null}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-xs font-semibold text-ink-700">
            Copiar os valores de
          </legend>
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="radio"
              name="origem-do-rascunho"
              checked={origem === 'ativa'}
              onChange={() => setOrigem('ativa')}
            />
            {temAtiva ? 'Versao ativa' : 'Valores documentados (nao ha versao ativa)'}
          </label>
          {padraoGlobalDisponivel ? (
            <label className="flex items-center gap-2 text-sm text-ink-800">
              <input
                type="radio"
                name="origem-do-rascunho"
                checked={origem === 'padrao_global'}
                onChange={() => setOrigem('padrao_global')}
              />
              Padrao global
            </label>
          ) : null}
        </fieldset>
        <Textarea
          label="Observacao"
          help={false}
          value={observacao}
          rows={3}
          onChange={(event) => setObservacao(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * Ativar o rascunho, com justificativa e, onde a governanca existe, inicio de
 * vigencia. Data futura deixa a versao agendada; sem data, vale agora.
 *
 * Quando a plataforma exige mais de um aprovador e o rascunho ja tem aprovacao, a
 * vigencia e a pedida na primeira: o servidor recusa outra data, que seria outra
 * decisao. O dialogo mostra as aprovacoes dadas e manda a mesma data.
 */
export function AtivarDialog({
  aberto,
  aoFechar,
  escopo,
  rascunho,
  vigenciaDisponivel,
  aoAtivar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  escopo: EscopoDoEditor;
  rascunho: CreditParameterSet;
  vigenciaDisponivel: boolean;
  aoAtivar: (versao: CreditParameterSet) => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const [vigencia, setVigencia] = useState('');
  const [tentou, setTentou] = useState(false);

  const aprovacoes = rascunho.approvals;
  const primeira = aprovacoes[0];
  // Com aprovacao dada, a data e a da primeira aprovacao, exatamente como gravada.
  const dataDaPrimeira = primeira ? primeira.requested_effective_from : undefined;
  const instante =
    dataDaPrimeira !== undefined
      ? dataDaPrimeira
        ? new Date(dataDaPrimeira)
        : null
      : vigencia
        ? new Date(vigencia)
        : null;
  const vigenciaInvalida = instante !== null && Number.isNaN(instante.getTime());
  const semJustificativa = justificativa.trim() === '';

  const ativar = useMutation({
    mutationFn: () =>
      escopo.api.activate(rascunho.id, {
        justification: justificativa.trim(),
        ...(dataDaPrimeira
          ? { effective_from: dataDaPrimeira }
          : instante && !vigenciaInvalida
            ? { effective_from: instante.toISOString() }
            : {}),
      }),
    onSuccess: (versao) => {
      setJustificativa('');
      setVigencia('');
      setTentou(false);
      aoAtivar(versao);
    },
  });

  const futura = instante !== null && !vigenciaInvalida && noFuturo(instante);

  return (
    <Modal
      open={aberto}
      onClose={aoFechar}
      title={`Ativar a versao ${rascunho.version}`}
      description="A versao ativa anterior passa a revogada e continua guardada, com as analises que a usaram."
      footer={
        <>
          <Button variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            loading={ativar.isPending}
            onClick={() => {
              setTentou(true);
              if (semJustificativa || vigenciaInvalida) return;
              ativar.mutate();
            }}
          >
            {futura ? 'Agendar versao' : 'Ativar versao'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {ativar.isError ? (
          <Alert tone="danger" title="Nao foi possivel ativar">
            {mensagemDoErro(ativar.error)}
          </Alert>
        ) : null}
        {primeira ? (
          <Alert
            tone="info"
            title={`Esta versao ja tem ${textoDasAprovacoes(aprovacoes.length)}`}
          >
            <ul className="flex flex-col gap-1">
              {aprovacoes.map((aprovacao) => (
                <li key={`${aprovacao.label}:${aprovacao.at}`}>
                  {`${aprovacao.label || 'Sem nome'} em ${formatDateTime(aprovacao.at)}: ${aprovacao.note}`}
                </li>
              ))}
            </ul>
            <p className="mt-2">
              {primeira.requested_effective_from
                ? `A vigencia e a pedida na primeira aprovacao: ${formatDateTime(primeira.requested_effective_from)}. Para outra data, edite o rascunho e aprove de novo.`
                : 'A primeira aprovacao pediu vigencia imediata: a versao vale quando a ultima aprovacao for dada. Para outra data, edite o rascunho e aprove de novo.'}
            </p>
          </Alert>
        ) : null}
        <Textarea
          label="Justificativa"
          help={false}
          value={justificativa}
          rows={3}
          error={
            tentou && semJustificativa ? 'A ativacao exige justificativa.' : undefined
          }
          hint="Por que esta versao entra em vigor. Fica na trilha de auditoria."
          onChange={(event) => setJustificativa(event.target.value)}
        />
        {vigenciaDisponivel && !primeira ? (
          <Field
            label="Inicio da vigencia (opcional)"
            help={false}
            type="datetime-local"
            value={vigencia}
            error={vigenciaInvalida ? 'Data e hora invalidas.' : undefined}
            hint={
              futura && instante
                ? `A versao fica agendada e entra em vigor em ${formatDateTime(instante.toISOString())}.`
                : 'Sem data, ou com data ja passada, a versao entra em vigor agora.'
            }
            onChange={(event) => setVigencia(event.target.value)}
          />
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Cancelar o agendamento de uma versao agendada, antes da data. A versao passa a
 * revogada sem ter valido, e a ativa atual continua valendo. Exige justificativa.
 */
export function CancelarAgendamentoDialog({
  versao,
  aoFechar,
  escopo,
  aoCancelar,
}: {
  versao: CreditParameterSet | null;
  aoFechar: () => void;
  escopo: EscopoDoEditor;
  aoCancelar: (versao: CreditParameterSet) => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const [tentou, setTentou] = useState(false);
  const semJustificativa = justificativa.trim() === '';

  const cancelar = useMutation({
    mutationFn: (alvo: CreditParameterSet) =>
      escopo.api.cancelSchedule(alvo.id, { justification: justificativa.trim() }),
    onSuccess: (cancelada) => {
      setJustificativa('');
      setTentou(false);
      aoCancelar(cancelada);
    },
  });

  function fechar() {
    cancelar.reset();
    setTentou(false);
    aoFechar();
  }

  return (
    <Modal
      open={versao !== null}
      onClose={fechar}
      title={
        versao
          ? `Cancelar o agendamento da versao ${versao.version}`
          : 'Cancelar agendamento'
      }
      description="A versao agendada passa a revogada sem ter valido. A versao ativa continua valendo."
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={cancelar.isPending}
            onClick={() => {
              setTentou(true);
              if (semJustificativa || !versao) return;
              cancelar.mutate(versao);
            }}
          >
            Cancelar agendamento
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {cancelar.isError ? (
          <Alert tone="danger" title="Nao foi possivel cancelar o agendamento">
            {mensagemDoErro(cancelar.error)}
          </Alert>
        ) : null}
        {versao?.effective_from ? (
          <p className="text-sm text-ink-700">
            {`Entraria em vigor em ${formatDateTime(versao.effective_from)}.`}
          </p>
        ) : null}
        <Textarea
          label="Justificativa do cancelamento"
          help={false}
          value={justificativa}
          rows={3}
          error={
            tentou && semJustificativa
              ? 'O cancelamento exige justificativa.'
              : undefined
          }
          hint="Por que a versao nao vai mais entrar em vigor. Fica na trilha de auditoria."
          onChange={(event) => setJustificativa(event.target.value)}
        />
      </div>
    </Modal>
  );
}
